import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { useTranslation } from '@tyro/i18n';
import type { ReturnTypeFromUseDocumentationPageDetail } from '../../api/pages';
import { toCssAspectRatio } from '../../utils/video-format';
import { isAllowedVideoHost } from '../editor/video-hero-block';

type PageVideoData = NonNullable<
  NonNullable<ReturnTypeFromUseDocumentationPageDetail>['page']
>['videoData'];

interface ReaderVideoEmbedProps {
  videoData: PageVideoData | null;
  title: string;
}

export function ReaderVideoEmbed({ videoData, title }: ReaderVideoEmbedProps) {
  const { t } = useTranslation(['documentation']);
  if (!videoData?.embedUrl) return null;
  if (!isAllowedVideoHost(videoData.embedUrl)) {
    return (
      <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>
        {t('documentation:editor.video.allowlistError')}
      </Alert>
    );
  }
  const aspectRatio = toCssAspectRatio(videoData.aspectRatio);
  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        maxHeight: '80vh',
        aspectRatio,
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'common.black',
      }}
    >
      <iframe
        src={videoData.embedUrl}
        title={title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          border: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: 8,
          left: 8,
          maxWidth: 'calc(100% - 16px)',
          px: 1,
          py: 0.25,
          bgcolor: 'rgba(0, 0, 0, 0.45)',
          borderRadius: 1,
        }}
      >
        <Link
          href={videoData.sourceUrl}
          target="_blank"
          rel="noopener noreferrer"
          underline="none"
        >
          <Typography
            variant="caption"
            sx={{
              color: 'common.white',
              opacity: 0.75,
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {videoData.sourceUrl}
          </Typography>
        </Link>
      </Box>
    </Box>
  );
}
